import { Select } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import Typo from 'consts/typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { selectAssets, selectCoins } from 'features/home/homeSlice';
import { getLocalStorage } from 'utils/sessionStorage';
import { IMG_ALT } from 'consts/app';
import SuccessIcon from 'assets/icons/SuccessIcon';
import useUserDetail from 'hooks/useUserDetail';
import useThemes from 'hooks/useThemes';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/userSlice';
import { formatCryptoBalance } from 'utils/formatAmount';
import {
  useAccount,
  useBalance,
  useCall,
  useReadContracts,
  useSendTransaction,
  useSwitchChain,
  useWriteContract,
} from 'wagmi';
import { erc20Abi, parseEther, parseUnits } from 'viem';
import depositApi from 'api/depositApi';
import { isValidAmount } from './WithdrawModal';
import CryptoWalletModal from './CryptoWalletModal';
import CustomAmountButton from 'components/Commons/CustomAmountButton';
import { showConfirmModal, showErrorModal } from 'components/Modals/CommonModals';
import InfomationIcon from 'assets/icons/InfomationIcon';
// import CustomAmountButton from 'components/Commons/CustomAmountButton';

interface CryptoWalletDepositModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

const CryptoWalletDepositModal: React.FunctionComponent<CryptoWalletDepositModalProps> = (
  props,
) => {
  const [currentAsset, setCurrentAsset] = useState<number>(0);
  const [currentCoin, setCurrentCoin] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const [hash, setHash] = useState<string>();
  const { title = '', visible, closeModal, className = '' } = props;
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const accessToken = getLocalStorage('access_token_terabet');
  const coins = useAppSelector(selectCoins);
  const assets = useAppSelector(selectAssets);
  const t = useAppTranslation();
  const et = useErrTranslation();
  const { isUserLoggedIn } = useUserDetail();
  const { currentTheme } = useThemes();
  // const currentUser = useSelector(selectCurrentUser);
  const { address, chainId } = useAccount();
  const { writeContractAsync } = useWriteContract();
  const [userAddress, setUserAddress] = useState<`0x${string}`>('0x');
  const [isOpenCryptoWalletModal, setIsOpenCryptoWalletModal] = useState<boolean>(false);
  const { switchChainAsync } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransaction();

  const result = useReadContracts({
    allowFailure: true,
    contracts: [
      {
        address: assets.find((x) => x.assetId === currentAsset)?.contractAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address || '0x'],
        chainId: assets.find((x) => x.assetId === currentAsset)?.chain.chainId,
      },
      {
        address: assets.find((x) => x.assetId === currentAsset)?.contractAddress,
        abi: erc20Abi,
        functionName: 'decimals',
        chainId: assets.find((x) => x.assetId === currentAsset)?.chain.chainId,
      },
      {
        address: assets.find((x) => x.assetId === currentAsset)?.contractAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [userAddress, address || '0x'],
        chainId: assets.find((x) => x.assetId === currentAsset)?.chain.chainId,
      },
    ],
  });

  const resultNativeBalance = useBalance({
    address,
    chainId: assets.find((x) => x.assetId === currentAsset)?.chain.chainId || -1,
  });

  const coinOptions = useMemo(() => {
    const priorityCoins = ['usdt', '$tbet', 'pyusd'];
    return coins
      .filter((x) => x.enableDeposit)
      .sort((a, b) => {
        const aPriority = priorityCoins.includes(a.symbol.toLowerCase()) ? 0 : 1;
        const bPriority = priorityCoins.includes(b.symbol.toLowerCase()) ? 0 : 1;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return 0;
      })
      .map((coin) => ({
        value: coin.id,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={coin.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {coin.symbol}
            </span>
          </div>
        ),
        symbol: coin.symbol,
        minAmount: coin.minAmount,
        maxAmount: coin.maxAmount,
      }));
  }, [coins, isPhone, isTablet]);

  const currentCrypto = useMemo(() => {
    const cr = coinOptions.find((c) => c.value === currentCoin);

    return cr;
  }, [currentCoin, coinOptions]);

  const networkOptions = useMemo(() => {
    return assets
      .filter((asset) => asset.coin.id === currentCoin)
      .map((asset) => ({
        value: asset.assetId,
        label: (
          <span
            className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
          >
            {asset.chain.name}
          </span>
        ),
      }));
  }, [assets, currentCoin, isPhone, isTablet]);

  useEffect(() => {
    if (coins.length !== 0) {
      setCurrentCoin(coins.find((coin) => coin.enableDeposit && coin.id === 4)?.id || 0);
    }
  }, [coins]);

  useEffect(() => {
    if (assets.length !== 0) {
      setCurrentAsset(assets.find((asset) => asset.coin.id === currentCoin)?.assetId || 0);
    }
  }, [currentCoin, assets]);

  useEffect(() => {
    getUserAddress();
  }, [currentAsset]);

  const onAssetChange = (event: number) => {
    setCurrentAsset(event);
  };
  const onCoinChange = (event: number) => {
    setCurrentCoin(event);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*[.,]?[0-9]{0,9}$/;
    const value = event.target.value.replace(',', '.');

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const getUserAddress = async () => {
    const { ok, body } = await depositApi.getAddress(
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
      currentAsset,
    );

    if (ok && body) {
      setUserAddress(body.address);
    }
  };

  const onSubmit = useCallback(async () => {
    const t = assets.find((x) => x.assetId === currentAsset);
    if (!t) return;
    setIsLoading(true);
    try {
      if (chainId !== assets.find((x) => x.assetId === currentAsset)?.chain.chainId) {
        await switchChainAsync({
          chainId: assets.find((x) => x.assetId === currentAsset)?.chain.chainId || 0,
        });
      }

      let res;
      if (t.contractAddress) {
        res = await writeContractAsync({
          chainId: assets.find((x) => x.assetId === currentAsset)?.chain.chainId,
          abi: erc20Abi,
          address: assets.find((x) => x.assetId === currentAsset)?.contractAddress || '0x',
          functionName: 'transfer',
          args: [userAddress || '0x', parseUnits(amount, result?.data?.[1]?.result || 18)],
        });
      } else {
        res = await sendTransactionAsync({
          to: userAddress,
          value: parseEther(amount),
        });
      }
      setHash(res);
      setIsShowSuccess(true);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  }, [amount, currentAsset]);

  const onCloseModal = () => {
    resetModal();
    closeModal();
  };

  const resetModal = () => {
    setHash('');
    setAmount('');
    setIsShowSuccess(false);
  };

  const getCryptoBalance = () => {
    const t = assets.find((x) => x.assetId === currentAsset);
    if (!t) return 0;
    if (t.contractAddress) {
      return formatCryptoBalance(result?.data?.[0]?.result, result?.data?.[1]?.result);
    } else if (!resultNativeBalance.isError) {
      return formatCryptoBalance(resultNativeBalance?.data?.value, result?.data?.[1]?.result);
    }
    return 0;
  };

  const isValid = () => {
    return !isLoading && Number(amount) !== 0 && Number(amount) <= Number(getCryptoBalance());
  };

  const handleCryptoWalletClick = () => {
    setIsOpenCryptoWalletModal(!isOpenCryptoWalletModal);
  };

  const handleSetField = (value: string) => {
    setAmount(value);
  };

  const handleDoubleClickOption = useCallback(
    (value: string) => {
      if (!isValid()) {
        showErrorModal({ content: 'Not enough balance to deposite', title: 'Error' });
      } else {
        showConfirmModal({
          content: (
            <p>
              {t('confirmModal.deposit.message', {
                amount: `${value} ${currentCrypto?.symbol}`,
              })}
            </p>
          ),
          width: 360,
          icon: <InfomationIcon />,
          onOk: onSubmit,
          okText: 'Submit',
          cancelText: 'Cancel',
        });
      }
    },
    [amount],
  );

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {!isShowSuccess ? (
        <div className="center-content-col gap-3">
          <div className="common-select">
            <p className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
              {t('cashier.depositOption')}
            </p>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentCoin}
              onChange={onCoinChange}
              options={coinOptions}
            />
          </div>
          <div className="common-select">
            <p className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
              {t('cashier.network')}
            </p>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentAsset}
              onChange={onAssetChange}
              options={networkOptions}
            />
          </div>
          <div className="common-input input-amount">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('cashier.amount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              onChange={onChange}
              value={amount}
              placeholder={t('placeholder.enterAmount')}
            />
          </div>
          <div className="common-input input-amount">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            ></label>
            {/* <CustomAmountButton
              symbol={currentCrypto?.symbol}
              onSetField={handleSetField}
              onOptionDoubleClick={handleDoubleClickOption}
              classes="custom-button-swap custom-button-modal"
            /> */}
          </div>

          {isUserLoggedIn && (
            <>
              <div className="withdraw-balance">
                <div className="withdraw-balance__left"></div>
                <div className="withdraw-balance__right">
                  <p
                    className={`${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2} font-normal`}
                  >{`${t('balance')}: ${getCryptoBalance()} ${assets.find(
                    (x) => x.assetId === currentAsset,
                  )?.coin.symbol}`}</p>
                </div>
              </div>
              <div className="withdraw-balance">
                <div className="withdraw-balance__left"></div>
                <div className="withdraw-balance__right">
                  <button
                    disabled={address && !isValid()}
                    className={`button-generate ${address && !isValid() ? 'disabled' : ''}`}
                    type="submit"
                    onClick={address ? onSubmit : handleCryptoWalletClick}
                  >
                    {address ? t('deposit.title') : t('common.connectWallet')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="center-content-col gap-3 withdraw--success text-center">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('deposit.success.title')}</p>
          <p className="message">{`$${amount} ${t('deposit.success.message')}`}</p>
          <p className="message word-break">Txn Hash: {hash}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('deposit.close')}
          </button>
        </div>
      )}
      <CryptoWalletModal isOpen={isOpenCryptoWalletModal} onCancel={handleCryptoWalletClick} />
    </Modal>
  );
};

export default CryptoWalletDepositModal;
